import { z } from "zod";
import { dateOnlySchema, listQuerySchema, timestampSchema, uuidSchema } from "./api.ts";
import { queryBooleanSchema, timestampsShape, userRefSchema } from "./common.ts";
import { currencyCodeSchema, minorSchema } from "./money.ts";
import { approvalStateSchema } from "./time.ts";

/**
 * EXPENSES AND REBILLING (PRD §7 Item 10, US-10.5 / US-10.7).
 *
 * An expense is a cost the agency incurred for a client: a freelancer's bill,
 * a stock image, a print run, travel. Three facts hang off it and each has a
 * different owner:
 *
 *  · the **cost** (`amountMinor` + `taxMinor`) — what profitability subtracts;
 *  · the **recharge** (`billable` + `markupBps`) — what the client is invoiced,
 *    stamped with `invoiceId` through the same locked seam time uses, so one
 *    receipt can never be rebilled twice;
 *  · the **reimbursement** (`paidBy` + `reimbursable`) — whether a team member
 *    is owed the money back.
 *
 * Money is masked (`null`) for a caller without `financials:view`.
 */

/** Who actually paid. A `member` expense is normally reimbursable. */
export const EXPENSE_PAYERS = ["company", "member"] as const;
export type ExpensePayer = (typeof EXPENSE_PAYERS)[number];
export const expensePayerSchema = z.enum(EXPENSE_PAYERS);

/** `not_billable` = never charged on; `unbilled` = waiting for an invoice; `invoiced` = owned by one. */
export const EXPENSE_REBILL_STATES = ["not_billable", "unbilled", "invoiced"] as const;
export type ExpenseRebillState = (typeof EXPENSE_REBILL_STATES)[number];
export const expenseRebillStateSchema = z.enum(EXPENSE_REBILL_STATES);

/**
 * How profitability treats a category. `freelancer` is reported separately
 * because it is the agency's biggest variable cost; `passthrough` is client
 * money the agency merely handles (ad spend), so it never touches margin.
 */
export const EXPENSE_COST_TYPES = ["overhead", "freelancer", "passthrough"] as const;
export type ExpenseCostType = (typeof EXPENSE_COST_TYPES)[number];
export const expenseCostTypeSchema = z.enum(EXPENSE_COST_TYPES);

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export const expenseCategorySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  code: z.string().nullable(),
  costType: expenseCostTypeSchema,
  sacCode: z.string().nullable(),
  billableByDefault: z.boolean(),
  defaultMarkupBps: z.number().int(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type ExpenseCategoryDto = z.infer<typeof expenseCategorySchema>;

const categoryWritableShape = {
  name: z.string().trim().min(1).max(120),
  code: z.string().trim().max(40).nullable(),
  costType: expenseCostTypeSchema,
  sacCode: z.string().trim().regex(/^\d{6}$/, "Use a 6-digit SAC code").nullable(),
  billableByDefault: z.boolean(),
  defaultMarkupBps: z.number().int().min(0).max(100000),
  sortOrder: z.number().int().min(0).max(9999),
  isActive: z.boolean(),
};

/** POST /api/v1/expense-categories (perm `expenses:approve`) → 201 Category. Duplicate name → CONFLICT. */
export const createExpenseCategoryInputSchema = z
  .object({
    ...categoryWritableShape,
    code: categoryWritableShape.code.optional(),
    costType: categoryWritableShape.costType.optional(),
    sacCode: categoryWritableShape.sacCode.optional(),
    billableByDefault: categoryWritableShape.billableByDefault.optional(),
    defaultMarkupBps: categoryWritableShape.defaultMarkupBps.optional(),
    sortOrder: categoryWritableShape.sortOrder.optional(),
    isActive: categoryWritableShape.isActive.optional(),
  })
  .strict();
export type CreateExpenseCategoryInput = z.input<typeof createExpenseCategoryInputSchema>;

/** PATCH /api/v1/expense-categories/:id (perm `expenses:approve`) → Category. */
export const updateExpenseCategoryInputSchema = z.object(categoryWritableShape).partial().strict();
export type UpdateExpenseCategoryInput = z.input<typeof updateExpenseCategoryInputSchema>;

// ---------------------------------------------------------------------------
// Expenses
// ---------------------------------------------------------------------------

export const expenseSchema = z.object({
  id: uuidSchema,
  reference: z.string(),
  clientId: uuidSchema.nullable(),
  projectId: uuidSchema.nullable(),
  projectName: z.string().nullable(),
  categoryId: uuidSchema.nullable(),
  categoryName: z.string().nullable(),
  costType: expenseCostTypeSchema,
  vendor: z.string().nullable(),
  description: z.string(),
  spentOn: dateOnlySchema,
  amountMinor: minorSchema.nullable(),
  /** Input GST paid on the bill, claimable as ITC. Excluded from cost. */
  taxMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
  billable: z.boolean(),
  markupBps: z.number().int(),
  /** amountMinor grossed up by the markup — what the client is charged. */
  rebillMinor: minorSchema.nullable(),
  rebillState: expenseRebillStateSchema,
  invoiceId: uuidSchema.nullable(),
  invoiceNumber: z.string().nullable(),
  receiptFileId: uuidSchema.nullable(),
  paidBy: expensePayerSchema,
  paidByUserId: uuidSchema.nullable(),
  paidByUser: userRefSchema.nullable(),
  reimbursable: z.boolean(),
  reimbursedAt: timestampSchema.nullable(),
  approvalState: approvalStateSchema,
  approvedByUserId: uuidSchema.nullable(),
  approvedAt: timestampSchema.nullable(),
  reviewNote: z.string().nullable(),
  submittedByUserId: uuidSchema.nullable(),
  notes: z.string().nullable(),
  /** True once an invoice owns the recharge — edits answer 409 naming it. */
  locked: z.boolean(),
  lockReason: z.string().nullable(),
  version: z.number().int(),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type ExpenseDto = z.infer<typeof expenseSchema>;

/** Shape WITHOUT defaults, so `.partial()` below cannot resurrect them (zod 4). */
const expenseWritableShape = {
  clientId: uuidSchema.nullable(),
  projectId: uuidSchema.nullable(),
  categoryId: uuidSchema.nullable(),
  vendor: z.string().trim().max(200).nullable(),
  description: z.string().trim().min(1).max(500),
  spentOn: dateOnlySchema,
  amountMinor: minorSchema.min(0),
  taxMinor: minorSchema.min(0),
  currency: currencyCodeSchema,
  billable: z.boolean(),
  markupBps: z.number().int().min(0).max(100000),
  receiptFileId: uuidSchema.nullable(),
  paidBy: expensePayerSchema,
  paidByUserId: uuidSchema.nullable(),
  reimbursable: z.boolean(),
  notes: z.string().trim().max(2000).nullable(),
};

/** POST /api/v1/expenses (perm `expenses:write`) → 201 Expense. */
export const createExpenseInputSchema = z
  .object({
    ...expenseWritableShape,
    clientId: expenseWritableShape.clientId.optional(),
    projectId: expenseWritableShape.projectId.optional(),
    categoryId: expenseWritableShape.categoryId.optional(),
    vendor: expenseWritableShape.vendor.optional(),
    taxMinor: expenseWritableShape.taxMinor.optional(),
    currency: expenseWritableShape.currency.optional(),
    billable: expenseWritableShape.billable.optional(),
    markupBps: expenseWritableShape.markupBps.optional(),
    receiptFileId: expenseWritableShape.receiptFileId.optional(),
    paidBy: expenseWritableShape.paidBy.optional(),
    paidByUserId: expenseWritableShape.paidByUserId.optional(),
    reimbursable: expenseWritableShape.reimbursable.optional(),
    notes: expenseWritableShape.notes.optional(),
    /** Submit for approval straight away instead of saving a draft. */
    submit: z.boolean().optional(),
  })
  .strict();
export type CreateExpenseInput = z.input<typeof createExpenseInputSchema>;

/** PATCH /api/v1/expenses/:id (perm `expenses:write`) → Expense. Rebilled → 409 naming the invoice. */
export const updateExpenseInputSchema = z
  .object({ ...expenseWritableShape, version: z.number().int().min(1) })
  .partial()
  .strict();
export type UpdateExpenseInput = z.input<typeof updateExpenseInputSchema>;

export const listExpensesQuerySchema = listQuerySchema.extend({
  clientId: uuidSchema.optional(),
  projectId: uuidSchema.optional(),
  categoryId: uuidSchema.optional(),
  paidByUserId: uuidSchema.optional(),
  approvalState: approvalStateSchema.optional(),
  rebillState: expenseRebillStateSchema.optional(),
  billable: queryBooleanSchema.optional(),
  reimbursable: queryBooleanSchema.optional(),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
  includeArchived: z.enum(["true", "false"]).optional(),
  sort: z.enum(["spentOn", "amountMinor", "createdAt"]).optional(),
});
export type ListExpensesQuery = z.infer<typeof listExpensesQuerySchema>;

/** GET /api/v1/expenses → page plus the totals of the WHOLE filter, not the page. */
export const expensePageSchema = z.object({
  items: z.array(expenseSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
  totalMinor: minorSchema.nullable(),
  taxMinor: minorSchema.nullable(),
  rebillMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
});
export type ExpensePage = z.infer<typeof expensePageSchema>;

/** POST /api/v1/expenses/:id/submit (perm `expenses:write`) → Expense in `submitted`. */
export const submitExpenseInputSchema = z.object({ note: z.string().trim().max(1000).nullable().optional() }).strict();
export type SubmitExpenseInput = z.input<typeof submitExpenseInputSchema>;

/** POST /api/v1/expenses/:id/approve|reject (perm `expenses:approve`). Rejecting needs a note. */
export const reviewExpenseInputSchema = z.object({ note: z.string().trim().max(1000).nullable().optional() }).strict();
export type ReviewExpenseInput = z.input<typeof reviewExpenseInputSchema>;

/**
 * POST /api/v1/expenses/rebill (perm `invoices:write`) → BillResult.
 * Goes through the very same locked seam time uses, so an expense can never be
 * charged onto two documents.
 */
export const rebillExpensesInputSchema = z.object({ invoiceId: uuidSchema, expenseIds: z.array(uuidSchema).min(1).max(2000) }).strict();
export type RebillExpensesInput = z.input<typeof rebillExpensesInputSchema>;
